import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeProvider';
import { useLocale } from '../context/DynamicLocaleProvider';
import { useUser } from '../context/UserContext';
import { getLocalizedUserRoleDisplayName } from '../utils/userRoles';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import styles from './UserTile.module.css';

interface InsolvencyUser {
  id: string;
  name: string;
  status: 'invited' | 'active' | 'blocked' | 'archive';
  avatar?: string; // Avatar image URL
  email?: string; // User email
  role?: string; // User role
  company?: string; // Company ID
}

interface UserTileProps {
  user: InsolvencyUser;
  onClick?: () => void;
  disabled?: boolean;
}

// Mock data and hooks for demonstration
const useInsolvencies = () => {
  const [users] = useState<InsolvencyUser[]>([
    {
      id: '1',
      name: 'John Doe',
      status: 'active',
      email: 'john.doe@example.com'
    },
    {
      id: '2',
      name: 'Jane Smith',
      status: 'invited',
      email: 'jane.smith@example.com'
    }
  ]);

  return { users, loading: false };
};



export function UserTile({ user, onClick, disabled = false }: UserTileProps) {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const navigate = useNavigate();
  const { t } = useLocale();
  const { company: currentUserCompany } = useUser();
  const [sponsorshipBadge, setSponsorshipBadge] = useState<{ enabled: boolean; text: string; logoUrl: string } | null>(null);

  // Fetch sponsorship badge from user's company
  // Use user.company if available, otherwise fall back to current user's company
  useEffect(() => {
    const fetchSponsorshipBadge = async (userCompanyId: string) => {
      try {
        const companyRef = doc(db, 'companies', userCompanyId);
        const companyDoc = await getDoc(companyRef);
        if (!companyDoc.exists()) return;
        
        const companyData = companyDoc.data() as any;
        const sponsorshipSettings = companyData?.settings?.sponsorshipBadge;
        if (!sponsorshipSettings) return;
        
        const badge = {
          enabled: sponsorshipSettings.enabled || false,
          text: sponsorshipSettings.text || '',
          logoUrl: sponsorshipSettings.logoUrl || ''
        };
        
        if (badge.logoUrl) {
          setSponsorshipBadge(badge);
        }
      } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error fetching sponsorship badge:', error);
      }
      }
    };

    const companyId = user.company || currentUserCompany;
    if (companyId) {
      fetchSponsorshipBadge(companyId);
    }
  }, [user.company, currentUserCompany]);

  const getLocalizedStatus = (status: InsolvencyUser['status']): string => {
    switch (status) {
      case 'active':
        return t('status_active');
      case 'invited':
        return t('status_invited');
      case 'blocked':
        return t('status_blocked');
      case 'archive':
      default:
        return t('status_archive');
    }
  };

  const handleClick = () => {
    if (disabled) return;
    
    if (onClick) {
      onClick();
    } else {
      // Navigate to user detail page
      navigate(`/user/${user.id}`);
    }
  };

  return (
    <div className={`${styles.userTile} ${disabled ? styles.disabled : ''}`} onClick={handleClick}>
      <div className={styles.userIcon}>
        {user.avatar ? (
          <img 
            src={user.avatar}
            alt="User Avatar" 
            width={16} 
            height={16} 
            className={styles.userAvatar}
          />
        ) : (
          <img 
            src={isDarkMode ? "/assets/icons/person.svg" : "/assets/icons/person.black.svg"} 
            alt="User" 
            width={12} 
            height={12} 
          />
        )}
      </div>
      
      <div className={styles.userInfo}>
        <h3 className={styles.userTitle}>
          {user.name || 'Unknown User'}
        </h3>
        <p className={styles.userDetails}>
          {user.email || 'No email'}
        </p>
      </div>
      
      <div className={styles.statusContainer}>

        {/* Status badge */}
        <div className={`${styles.statusBadge} ${styles[user.status || 'archive']}`}>
          {getLocalizedStatus(user.status || 'archive')}
        </div>

        {/* Role badge */}
        {user.role && (
          <div className={styles.roleBadge}>
            <img src="/assets/icons/apple.intelligence.green.svg" alt="Badge" width={16} height={16} />
            <span>{getLocalizedUserRoleDisplayName(user.role, t)}</span>
          </div>
        )}

        {/* Corporate identity */}
        {sponsorshipBadge?.logoUrl && (
          <div className={styles.sponsorLogo}>
            <img 
              src={sponsorshipBadge.logoUrl} 
              alt="Corporate identity" 
              height={20}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export function UserList({ showInviteButtons = false }: { showInviteButtons?: boolean }) {
  const { users, loading } = useInsolvencies();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [searchValue, setSearchValue] = useState('');

  // Filter and search logic
  const filteredUsers = useMemo(() => {
    return users.filter((user: InsolvencyUser) => {
      // Search filter
      const searchMatch = !searchValue || 
        user.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        false;

      return searchMatch;
    });
  }, [users, searchValue]);

  return (
    <div className={styles.userList}>
      {/* Simple search bar */}
      <div className={styles.searchBar}>
        <input
          type="text"
          placeholder="Search by name or age..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className={styles.searchInput}
        />
        
      </div>
      
      {loading ? (
        <div className={styles.loadingContainer}>
          <p>Loading users...</p>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className={styles.emptyContainer}>
          <img 
            src={isDarkMode ? "/assets/icons/person.svg" : "/assets/icons/person.black.svg"} 
            alt="No users" 
            width={48} 
            height={48} 
          />
          <h3>No users found</h3>
          <p>{users.length === 0 ? 'Users will appear here when you add them.' : 'No results match your filters'}</p>
        </div>
      ) : (
        filteredUsers.map((user: InsolvencyUser) => (
          <div key={user.id} className={styles.userTileContainer}>
            <UserTile user={user} />
            {showInviteButtons && user.status === 'invited' && (
              <button className={styles.inviteButton}>
                <img 
                  src={isDarkMode ? "/assets/icons/paperplane.svg" : "/assets/icons/paperplane.black.svg"} 
                  alt="Invite" 
                  width={14} 
                  height={14} 
                />
                Invite
              </button>
            )}
          </div>
        ))
      )}
    </div>
  );
}
